<template>
  <div>
    <div v-if="editedScouting">
      <div><strong>Plant:</strong> {{ plantName }}</div>
      <div><strong>Division:</strong> {{ divisionName }}</div>
      <div><strong>Location:</strong> {{ locationName }}</div>
    </div>
    <v-data-table
      :headers="summaryHeaders"
      :items="summaryRows"
      :rows-per-page-items="rowsPerPageItems"
      :pagination.sync="pagination"
    >
      <template slot="items" slot-scope="props">
        <td v-for="header in summaryHeaders" :key="header.value">
          {{ props.item[header.value] }}
        </td>
      </template>
      <template v-slot:no-data>
        <v-alert :value="true" color="secondary" icon="warning">
          No pests were recorded in this scouting.
        </v-alert>
      </template>
    </v-data-table>
  </div>
</template>

<script>
export default {
	name: "ScoutingSummary",
	props: {
		editedScouting: {
			required: true,
			default: null
		}
	},
	data() {
		return {
      pagination: {
        page: 1,
        rowsPerPage: 10,
        descending: false
      },
      rowsPerPageItems: [10, 25, 50, 100],
		};
	},
	computed: {
    locationName() {
      return this.editedScouting && this.editedScouting.LocationName
        ? this.editedScouting.LocationName
        : '';
    },
    divisionName() {
      return this.editedScouting && this.editedScouting.DivisionName
        ? this.editedScouting.DivisionName
        : '';
    },
    plantName() {
      return this.editedScouting && this.editedScouting.PlantName
        ? this.editedScouting.PlantName
        : '';
    },
		summaryHeaders() {
			if (!this.editedScouting || !this.editedScouting.ScoutingSpotChecks) return [];
			const spotCount = this.editedScouting.ScoutingSpotChecks.length;
			let headers = [
				{ text: 'Pest', value: 'pestName' }
			];
			for (let i = 0; i < spotCount; i++) {
				headers.push({ text: `Spot ${i + 1}`, value: `spot${i + 1}` });
			}
			return headers;
		},
		
    /**
     * Constructs the summary rows for the scouting summary table.
     * For each pest, the row contains the pest name and the observations from each spot check.
     * Number of spot checks determines the number of columns.
     * @returns {Array} Array of summary row objects.
     */
    summaryRows() {
      if (!this.editedScouting || !this.editedScouting.ScoutingSpotChecks) return [];
			// Sort spotChecks by Number ascending
			const spotChecks = [...this.editedScouting.ScoutingSpotChecks].sort((a, b) => a.Number - b.Number);

      // Collect all PestConfigurationIDs from all spot checks. 
      // I flatten the arrays so that I have a single array of all IDs; thus easier to get uniques.
      // The data structure is such that each spot check has an array of observations, 
      // and each observation has a PestConfigurationID.
      const allPestIDs = spotChecks
        .flatMap(spot => spot.ScoutingSpotCheckObservations.map(obs => obs.PestConfigurationID));

      const uniquePestIDs = Array.from(new Set(allPestIDs));

			let rows = [];

      // For each pestID, create a row with observations from each spot check
			uniquePestIDs.forEach(pestID => {
        const pestConfig = this.$store.getters['ScoutStore/getPestConfigurationByPestConfigurationID'](pestID);
				const pestName = pestConfig 
          ? pestConfig.Name
          : `Pest ${pestID}`;

				let row = { pestName };

        // Now we fill in the observations for each spot check
				spotChecks.forEach((spot, idx) => {
					const obs = spot.ScoutingSpotCheckObservations.find(o => o.PestConfigurationID === pestID);

          // If found, set the observation value; otherwise, leave it empty
					row[`spot${idx + 1}`] = (obs && obs.Observation > 0) 
            ? obs.Observation 
            : '';
				});

        // Only add the row if it has at least one non-empty observation
				const hasValue = Object.keys(row).some(k => 
          k !== 'pestName' // Exclude pestName from this check
          && row[k] !== '' // Check if there's a value
        );
				if (hasValue) {
					rows.push(row);
				}
			});
			return rows;
		}
	},
};
</script>
